import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { Button, Group } from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconRefresh,
  IconRotate,
  IconRotateClockwise,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import "./PDFViewer.scss";

interface PDFViewerProps {
  file: string;
  onLoad?: (dimensions: { width: number; height: number }) => void;
  url?: string;
  onError?: (error: Error) => void;
  simplifiedControls?: boolean; // Hide zoom/rotate controls
  disableDrag?: boolean; // Disable pan/drag functionality
}

const MIN_SCALE = 0.2;
const MAX_SCALE = 3;
const SCALE_STEP = 0.1;
const DEFAULT_PAGE_NUMBER = 1;
const INITIAL_SCALE = 1.0;

const PDFViewer: React.FC<PDFViewerProps> = ({
  file,
  onLoad,
  url,
  onError,
  simplifiedControls = false,
  disableDrag = false,
}) => {
  const [numPages, setNumPages] = useState<number | undefined>();
  const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [, setPageDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const resolvedUrl = url || file;
  const isImage = /\.(jpe?g|png)$/i.test(resolvedUrl);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setPageNumber(DEFAULT_PAGE_NUMBER);
    },
    []
  );

  const handlePageRenderSuccess = (page: any) => {
    const viewport = page.getViewport({ scale: 1.0 });
    setPageDimensions({
      width: viewport.width,
      height: viewport.height,
    });
    onLoad?.({ width: viewport.width, height: viewport.height });

    if (containerRef.current && scale === INITIAL_SCALE) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const scaleX = (containerWidth - 40) / viewport.width;
      const scaleY = (containerHeight - 40) / viewport.height;
      const newScale = Math.min(scaleX, scaleY, MAX_SCALE);

      if (newScale !== scale) {
        setScale(Math.max(newScale, MIN_SCALE));
      }
    }
  };

  const handleZoomIn = () =>
    setScale((s) => Math.min(s + SCALE_STEP, MAX_SCALE));
  const handleZoomOut = () =>
    setScale((s) => Math.max(s - SCALE_STEP, MIN_SCALE));
  const handleRotateLeft = () => setRotation((r) => (r - 90) % 360);
  const handleRotateRight = () => setRotation((r) => (r + 90) % 360);
  const handleReset = () => {
    setScale(INITIAL_SCALE);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handlePreviousPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disableDrag) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disableDrag || !isDragging) return;
    e.preventDefault();
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disableDrag) return;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handlePointerLeave = () => {
    if (disableDrag) return;
    setIsDragging(false);
  };

  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [scale]);

  return (
    <div className="pdf-viewer">
      <div className="controls-container">
        <Group justify="center" gap="xs">
          {!simplifiedControls && (
            <>
              <Button
                size="sm"
                onClick={handleZoomOut}
                disabled={scale <= MIN_SCALE}
                aria-label="Zoom out"
              >
                <IconZoomOut size={16} aria-hidden="true" />
              </Button>
              <span>{Math.round(scale * 100)}%</span>
              <Button
                size="sm"
                onClick={handleZoomIn}
                disabled={scale >= MAX_SCALE}
                aria-label="Zoom in"
              >
                <IconZoomIn size={16} aria-hidden="true" />
              </Button>
              <Button
                size="sm"
                onClick={handleRotateLeft}
                aria-label="Rotate counterclockwise"
              >
                <IconRotate size={16} aria-hidden="true" />
              </Button>
              <Button
                size="sm"
                onClick={handleRotateRight}
                aria-label="Rotate clockwise"
              >
                <IconRotateClockwise size={16} aria-hidden="true" />
              </Button>
              <Button size="sm" onClick={handleReset} aria-label="Reset view">
                <IconRefresh size={16} aria-hidden="true" />
              </Button>
            </>
          )}
          {numPages && numPages > 1 && (
            <>
              <Button
                size="sm"
                onClick={handlePreviousPage}
                disabled={pageNumber <= 1}
                aria-label="Previous page"
              >
                <IconArrowLeft size={16} aria-hidden="true" />
              </Button>
              <span>
                Page {pageNumber} of {numPages}
              </span>
              <Button
                size="sm"
                onClick={handleNextPage}
                disabled={pageNumber >= numPages}
                aria-label="Next page"
              >
                <IconArrowRight size={16} aria-hidden="true" />
              </Button>
            </>
          )}
        </Group>
      </div>

      <div className="content-container">
        <div
          ref={containerRef}
          className={`pdf-container ${isDragging ? "dragging" : ""} ${disableDrag ? "drag-disabled" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
        >
          <div
            style={{
              transform: `translate(${position.x}px, ${position.y}px)`,
              transformOrigin: "center center",
              transition: isDragging ? "none" : "transform 0.3s ease",
            }}
          >
            {isImage ? (
              <img
                src={resolvedUrl}
                alt="Scanned document"
                className="pdf-image-fallback"
              />
            ) : (
              <Document
                file={resolvedUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => {
                  console.error("[PDFViewer] Failed to load PDF:", {
                    error,
                    url,
                    resolvedUrl,
                  });
                  onError?.(error);
                }}
                loading={
                  <div className="loading" role="alert">
                    Loading PDF...
                  </div>
                }
                error={
                  <div className="error" role="alert">
                    Failed to load PDF
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  onRenderSuccess={handlePageRenderSuccess}
                  loading={
                    <div className="loading" role="alert">
                      Loading page...
                    </div>
                  }
                  error={
                    <div className="error" role="alert">
                      Failed to load page
                    </div>
                  }
                />
              </Document>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
